import type {
  CatalogItem,
  CatalogItemCategory,
  CatalogItemRule,
  CatalogFilterResponse,
  QuoteFormData,
  QuoteResult,
} from "@apex/shared";

/**
 * Widget view states
 */
export type ViewType =
  | "loading"
  | "selection"
  | "configure"
  | "summary"
  | "success"
  | "error"
  | "service-unavailable";

/**
 * Subtype of the service-unavailable view (#554). Drives the default
 * message rendered when the API rejects with a 403/429.
 */
export type ServiceUnavailableSubtype =
  | "subscription"
  | "origin"
  | "key"
  | "key-rotated"
  | "rate-limit";

/**
 * Per-subtype detail surfaced by ServiceUnavailableView. `message` overrides
 * the per-subtype default; `retryAfter` (seconds) is set for rate-limit
 * subtypes from the API's Retry-After header; `requestId` shown as a
 * support-reference footer when present.
 */
export interface ServiceUnavailableDetail {
  subtype: ServiceUnavailableSubtype;
  message?: string;
  retryAfter?: number;
  requestId?: string;
}

/**
 * Widget configuration passed via props
 */
export interface WidgetConfig {
  apiUrl: string;
  apiKey: string;
  /** Optional: Pre-select a root item by code */
  initialRootItem?: string;
  /**
   * Optional: which CatalogItem property `initialRootItem` refers to.
   * Defaults to "code". Set to "id" to preselect by the item's internal id
   * instead of its human-readable code.
   */
  initialItemProperty?: "code" | "id";
  /** Optional: Pre-select child items by codes */
  initialItems?: string[];
  /** Optional: Theme mode (light/dark/system). Public API on `<apex-widget>` HTML attribute. */
  theme?: "light" | "dark" | "system";
  /** Optional: Custom CSS class for the root element */
  className?: string;
  /**
   * Optional tenant slug (e.g. "ascentra"). Used to namespace persisted
   * sessionStorage state so multiple widgets sharing a domain do not bleed
   * into each other. When omitted we fall back to namespacing by the
   * API-key prefix alone, which is still per-tenant in practice.
   */
  tenantSlug?: string;
  /**
   * Per-tenant theme + form configuration. Sourced from `WidgetThemeConfig`
   * (#541) when that lands; today this defaults to sensible CPL B2B values
   * when unset. See WidgetTheme below. Distinct from `theme` (light/dark mode).
   */
  themeConfig?: WidgetTheme;
}

/**
 * Per-tenant theme + form configuration. Sourced from `WidgetThemeConfig`
 * on the backend via `GET /api/v1/widget/public-theme`, fetched by the
 * widget on mount. Host-supplied values (React/Vue SDK `themeConfig` prop)
 * override fetched values, so SDK consumers can still hand-roll the theme.
 */
export interface WidgetTheme {
  /** When true (default), the Company field on the quote form is required. */
  requireCompany?: boolean;
  /**
   * When true, the quote form must show a consent checkbox that the
   * customer ticks before submitting. Defaults to false. Tenants in
   * jurisdictions that require explicit opt-in flip this on.
   */
  requireExplicitConsent?: boolean;
  /** Override timeline copy on the success view. Falls back to default 3-line copy when null. */
  confirmationCopy?: string[];
  /** URL for the "Visit our website" CTA on the success view. CTA is hidden when null. */
  tenantWebsiteUrl?: string | null;
  /**
   * Tenant logo rendered in the widget header. Falls back to a text-only
   * header when null. Sourced from `WidgetThemeConfig.LogoUrl`.
   */
  logoUrl?: string | null;
  /**
   * Per-subtype message overrides for the service-unavailable view (#554).
   * Each entry, when set, replaces the spec default for that subtype. The
   * `{retryAfter}` token is substituted with a human-readable duration.
   */
  serviceUnavailableMessages?: Partial<
    Record<ServiceUnavailableSubtype, string>
  >;
}

/**
 * Theme value with all defaults applied — what consumers actually read.
 */
export interface ResolvedWidgetTheme {
  requireCompany: boolean;
  requireExplicitConsent: boolean;
  confirmationCopy: string[];
  tenantWebsiteUrl: string | null;
  logoUrl: string | null;
  serviceUnavailableMessages: Partial<
    Record<ServiceUnavailableSubtype, string>
  >;
}

/** Default copy for the success-view timeline (#539). */
export const DEFAULT_CONFIRMATION_COPY: ReadonlyArray<string> = [
  "Our team will review your configuration",
  "You'll receive a detailed quote via email",
  "A sales representative may contact you",
];

/**
 * Resolves a partial WidgetTheme with sensible defaults.
 *
 * The shape comes from `WidgetThemeConfig` on the backend (#541) and is passed
 * down as a `theme` prop on `<Widget />` from the host page (the embed snippet
 * fetches the public theme JSON before mounting). When fields are absent we
 * fall back to spec defaults: `RequireCompany=true`, the built-in 3-line
 * `confirmationCopy`, and a hidden "Visit our website" CTA.
 */
export function resolveWidgetTheme(theme?: WidgetTheme): ResolvedWidgetTheme {
  return {
    // Default true — matches CPL B2B; tenants can opt-out via WidgetThemeConfig.
    requireCompany: theme?.requireCompany ?? true,
    requireExplicitConsent: theme?.requireExplicitConsent ?? false,
    confirmationCopy:
      theme?.confirmationCopy && theme.confirmationCopy.length > 0
        ? theme.confirmationCopy
        : Array.from(DEFAULT_CONFIRMATION_COPY),
    tenantWebsiteUrl: theme?.tenantWebsiteUrl ?? null,
    logoUrl: theme?.logoUrl ?? null,
    serviceUnavailableMessages: theme?.serviceUnavailableMessages ?? {},
  };
}

/**
 * Widget state
 */
export interface WidgetState {
  /** Current view */
  view: ViewType;

  /** Available root items */
  rootItems: CatalogItem[];

  /** Available filters for root type */
  filters: CatalogFilterResponse | null;

  /**
   * Whether the root type opts into price display. Mirrored from
   * `CatalogItemType.hasPricing`; `CatalogItemCard` consults this so
   * tenants with HasPricing=false (CPL) see "Get a quote" instead of a
   * monetary price (#533).
   */
  rootHasPricing: boolean;

  /** Child item categories */
  categories: CatalogItemCategory[];

  /** Child items for selected root item */
  items: CatalogItem[];

  /** Compatibility rules for child items */
  itemRules: CatalogItemRule[];

  /** Default child item IDs from the API */
  defaultItemIds: string[];

  /** Currently selected root item */
  selectedRootItem: CatalogItem | null;

  /** Selected child item IDs */
  selectedItems: Set<string>;

  /** Quote form data (partial until submission) */
  quoteForm: Partial<QuoteFormData>;

  /** Active filter values (select filters: filterKey → option value) */
  activeFilters: Record<string, string>;

  /** Active range filter values (range filters: filterKey → {min, max}) */
  activeRangeFilters: Record<string, { min: number; max: number }>;

  /** Free-text search term applied on the Models step. Empty string == no filter. */
  searchTerm: string;

  /**
   * Free-text search term applied on the Configure step's accessory list.
   * Filters loaded compatible items client-side over name + code + description.
   * Empty string == no filter. Distinct from `searchTerm` (which targets the
   * Models grid) so the two searches don't bleed into each other.
   */
  accessorySearchTerm: string;

  /**
   * Active sort key for the Models grid. "featured" (default) sorts by
   * displayOrder; "name-asc" / "name-desc" sort by item name; range-derived
   * sort keys take the form `range:{filterKey}:asc|desc`.
   */
  sortKey: string;

  /** Loading state for async operations */
  isLoading: boolean;

  /** Submitting state for quote submission */
  isSubmitting: boolean;

  /** Error state */
  error: Error | null;

  /** Quote submission result */
  quoteResult: QuoteResult | null;

  /**
   * Anti-spam honeypot value (#538). Real users never fill this; bots usually
   * do. Forwarded to the server which silently drops the submission when set.
   * Kept on widget state so it doesn't leak into the public QuoteFormData shape.
   */
  honeypot: string;

  /**
   * Service-unavailable detail (#554). Set when the API returns a 403/429
   * with a structured error body; cleared on RESET. When non-null, view
   * should be "service-unavailable".
   */
  serviceUnavailable: ServiceUnavailableDetail | null;
}

/**
 * Initial state for the widget
 */
export const initialState: WidgetState = {
  view: "loading",
  rootItems: [],
  filters: null,
  rootHasPricing: true,
  categories: [],
  items: [],
  itemRules: [],
  defaultItemIds: [],
  selectedRootItem: null,
  selectedItems: new Set(),
  quoteForm: {},
  activeFilters: {},
  activeRangeFilters: {},
  searchTerm: "",
  accessorySearchTerm: "",
  sortKey: "featured",
  isLoading: true,
  isSubmitting: false,
  error: null,
  quoteResult: null,
  honeypot: "",
  serviceUnavailable: null,
};

/**
 * Widget actions
 */
export type WidgetAction =
  | { type: "INIT" }
  | {
      type: "DATA_LOADED";
      payload: {
        rootItems: CatalogItem[];
        filters: CatalogFilterResponse | null;
        categories: CatalogItemCategory[];
        rootHasPricing: boolean;
      };
    }
  | { type: "SELECT_ROOT_ITEM"; payload: CatalogItem }
  | {
      type: "ROOT_ITEM_LOADED";
      payload: {
        rootItem: CatalogItem;
        items: CatalogItem[];
        rules: CatalogItemRule[];
        defaultItemIds: string[];
        categories: CatalogItemCategory[];
      };
    }
  | { type: "TOGGLE_ITEM"; payload: string }
  | { type: "SET_SELECTED_ITEMS"; payload: string[] }
  | { type: "SET_FILTER"; payload: { key: string; value: string } }
  | {
      type: "SET_RANGE_FILTER";
      payload: { key: string; range: { min: number; max: number } };
    }
  | { type: "CLEAR_RANGE_FILTER"; payload: string }
  | { type: "CLEAR_FILTERS" }
  | { type: "SET_SEARCH_TERM"; payload: string }
  | { type: "SET_ACCESSORY_SEARCH_TERM"; payload: string }
  | { type: "SET_SORT_KEY"; payload: string }
  | { type: "UPDATE_QUOTE_FORM"; payload: Partial<QuoteFormData> }
  | { type: "SET_HONEYPOT"; payload: string }
  | { type: "VIEW_SUMMARY" }
  | { type: "SUBMIT_QUOTE" }
  | { type: "QUOTE_SUBMITTED"; payload: QuoteResult }
  | { type: "BACK_TO_SELECTION" }
  | { type: "BACK_TO_CONFIGURE" }
  | { type: "RESET" }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "ERROR"; payload: Error }
  | { type: "SERVICE_UNAVAILABLE"; payload: ServiceUnavailableDetail }
  | {
      /**
       * Bulk-replace selection-shaped state from persisted data
       * (sessionStorage + URL hash). Loaded catalog data (rootItems,
       * categories, items, filters) is preserved.
       */
      type: "HYDRATE";
      payload: {
        selectedItemIds: string[];
        view: ViewType;
        activeFilters: Record<string, string>;
        activeRangeFilters?: Record<string, { min: number; max: number }>;
        searchTerm?: string;
        sortKey?: string;
      };
    };

/**
 * Widget context value (state + dispatch + helpers)
 */
export interface WidgetContextValue {
  state: WidgetState;
  dispatch: React.Dispatch<WidgetAction>;

  /**
   * Resolved theme + form configuration (#537/#539). Today this is taken
   * from the WidgetConfig.theme prop or a default; once #541 lands these
   * values are sourced from `WidgetThemeConfig` on the backend.
   */
  theme: ResolvedWidgetTheme;

  // Helper methods for common operations
  selectRootItem: (rootItem: CatalogItem) => void;
  toggleItem: (id: string) => void;
  setSelectedItems: (ids: string[]) => void;
  setFilter: (key: string, value: string) => void;
  setRangeFilter: (key: string, range: { min: number; max: number }) => void;
  clearRangeFilter: (key: string) => void;
  clearFilters: () => void;
  setSearchTerm: (term: string) => void;
  setAccessorySearchTerm: (term: string) => void;
  setSortKey: (sortKey: string) => void;
  updateQuoteForm: (data: Partial<QuoteFormData>) => void;
  setHoneypot: (value: string) => void;
  viewSummary: () => void;
  submitQuote: () => Promise<void>;
  goBack: () => void;
  reset: () => void;

  // Computed values
  totalPrice: number;
  selectedItemList: CatalogItem[];
  filteredRootItems: CatalogItem[];

  // Item navigation
  scrollToItem: (id: string) => void;
  registerScrollToItem: (fn: (id: string) => void) => void;
}
